import { useEffect, useState } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { FileText, Newspaper, Video } from 'lucide-react';
import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer';
import { CONTENT_DOMAINS, formatDomainLabel } from '@/constants/contentDomains';
import useContentFilterOptions from '@/hooks/content/useContentFilterOptions';
import AllTopicsView from '@/modules/content/feed/filters/parts/AllTopicsView';
import FilterGroup from '@/modules/content/feed/filters/parts/FilterGroup';
import FilterPill from '@/modules/content/feed/filters/parts/FilterPill';
import { domainGlyph } from '@/modules/content/feed/filters/parts/domainGlyph';
import {
  EMPTY_CONTENT_FILTERS,
  filtersEqual,
  isFiltersActive,
  TContentFilters,
  toggleInSet,
  toggleSingle,
} from '@/modules/content/feed/filters/model/types';
import { TContentType } from '@/types/Content.types';
import { THealthDomain } from '@/types/Health.types';

const TYPE_OPTIONS: { value: TContentType; label: string; icon: React.ReactNode }[] = [
  { value: 'article', label: 'Article', icon: <Newspaper size={16} /> },
  { value: 'post', label: 'Post', icon: <FileText size={16} /> },
  { value: 'video', label: 'Reel', icon: <Video size={16} /> },
];

const CREATOR_OPTIONS = [
  { value: 'my_org' as const, label: 'My Organization', iconSrc: '/svg/handshake-icon.svg' },
  { value: 'platform' as const, label: 'MyOmnia', iconSrc: '/svg/myomnia-logo-icon.svg' },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
  applied: TContentFilters;
  onApply: (filters: TContentFilters) => void;
};

// The consolidated filter sheet. Edits a draft and only commits it on "Apply Filters" —
// so the grid doesn't churn while the user is still choosing. "Show All Topics" pushes an
// in-sheet sub-view rather than opening a second modal.
const ContentFilterModal = ({ isOpen, onClose, applied, onApply }: Props) => {
  const [draft, setDraft] = useState<TContentFilters>(applied);
  const [view, setView] = useState<'main' | 'allTopics'>('main');

  // Re-seed the draft from the applied set each time the sheet opens, so an abandoned
  // edit (closed without applying) is discarded rather than lingering.
  useEffect(() => {
    if (isOpen) {
      setDraft(applied);
      setView('main');
    }
  }, [isOpen, applied]);

  // The personalized short-list of topics for the main sheet.
  const { topics: personalizedTopics } = useContentFilterOptions();

  const setType = (type: TContentType) => setDraft((d) => ({ ...d, types: toggleInSet(d.types, type) }));
  const setDomains = (domain: THealthDomain) => setDraft((d) => ({ ...d, domains: toggleInSet(d.domains, domain) }));
  const setTopics = (topics: string[]) => setDraft((d) => ({ ...d, topics }));
  const setCreator = (creator: 'my_org' | 'platform') =>
    setDraft((d) => ({ ...d, creator: toggleSingle(d.creator, creator) }));

  // Apply is meaningful only when the draft actually differs from what's applied — so a
  // no-op (including clearing an already-empty set) leaves the button inert.
  const canApply = !filtersEqual(draft, applied);

  const apply = () => {
    onApply(draft);
    onClose();
  };

  // ModalContainer applies `width` as its min-width (default 100% → full viewport), so the
  // cap has to come from `width`, not `maxWidth` alone. `min(92vw, 560px)` keeps a
  // comfortable sheet on desktop and still leaves side margins on a phone.
  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      width="min(92vw, 560px)"
      maxWidth="560px"
      backgroundColor="#F0F8FF"
    >
      <Box paddingX={'14px'} paddingY="28px">
        {view === 'allTopics' ? (
          <AllTopicsView selected={draft.topics} onChange={setTopics} onBack={() => setView('main')} />
        ) : (
          <VStack align="stretch" gap="24px">
            <FilterGroup title="Type of Content">
              {TYPE_OPTIONS.map((option) => (
                <FilterPill
                  key={option.value}
                  label={option.label}
                  icon={option.icon}
                  selected={draft.types.includes(option.value)}
                  onClick={() => setType(option.value)}
                />
              ))}
            </FilterGroup>

            <FilterGroup title="Filter per Domain of Wholeness">
              {CONTENT_DOMAINS.map((domain) => (
                <FilterPill
                  key={domain}
                  label={formatDomainLabel(domain)}
                  icon={domainGlyph(domain)}
                  selected={draft.domains.includes(domain)}
                  onClick={() => setDomains(domain)}
                />
              ))}
            </FilterGroup>

            <FilterGroup title="Filter per Topic">
              {personalizedTopics.map((topic) => (
                <FilterPill
                  key={topic.value}
                  label={topic.label}
                  icon={topic.domain ? domainGlyph(topic.domain as THealthDomain) : undefined}
                  selected={draft.topics.includes(topic.value)}
                  onClick={() => setTopics(toggleInSet(draft.topics, topic.value))}
                />
              ))}
              <ShowAllTopicsButton onClick={() => setView('allTopics')} />
            </FilterGroup>

            <FilterGroup title="Filter per Creator">
              {CREATOR_OPTIONS.map((option) => (
                <FilterPill
                  key={option.value}
                  label={option.label}
                  icon={
                    <Box
                      as="img"
                      src={option.iconSrc}
                      width="16px"
                      height="16px"
                      sx={{ filter: 'brightness(0) invert(1)' }}
                    />
                  }
                  selected={draft.creator === option.value}
                  onClick={() => setCreator(option.value)}
                />
              ))}
            </FilterGroup>

            <VStack align="stretch" gap="8px" paddingTop="4px">
              <CustomButton
                label="Apply Filters"
                width="100%"
                disabled={!canApply}
                backgroundColor="secondary.500"
                hoverBackgroundColor="secondary.600"
                labelColor="white"
                onClick={apply}
              />
              {isFiltersActive(draft) && (
                <Text
                  as="button"
                  type="button"
                  onClick={() => setDraft(EMPTY_CONTENT_FILTERS)}
                  fontSize="13px"
                  fontWeight={700}
                  color="text.mediumBlue"
                  _hover={{ color: 'extra.red' }}
                >
                  Clear all
                </Text>
              )}
            </VStack>
          </VStack>
        )}
      </Box>
    </ModalContainer>
  );
};

const ShowAllTopicsButton = ({ onClick }: { onClick: () => void }) => (
  <HStack
    as="button"
    type="button"
    onClick={onClick}
    gap="8px"
    paddingX="16px"
    paddingY="10px"
    borderRadius="full"
    backgroundColor="white"
    boxShadow="0px 4px 14px rgba(14, 0, 98, 0.10)"
    _hover={{ filter: 'brightness(0.98)' }}
  >
    <Text fontSize="14px" fontWeight={700} color="text.darkBlue" whiteSpace="nowrap">
      Show All Topics →
    </Text>
  </HStack>
);

export default ContentFilterModal;
